import { FieldState, FormState } from 'formstate';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { injectable, inject } from '@servicetitan/react-ioc';
import {
    SubscriptionPreferencesApi as SubscriptionPreferences,
    UpdateSubscriptionPreferenceRequest,
} from '../api/subscription-preferences.api';
import { UnsubscribeApi as Unsubscribe, UnsubscribeRequest } from '../api/unsubscribe.api';
import { OptInApi as OptIn, OptInRequest } from '../api/optin.api';
import { SubscriptionOptions } from '../enums/subscription-options';
import { ValidateRequest, ValidationApi as Validation } from '../api/validation.api';

export enum LoadState {
    NotLoaded,
    Loaded,
    Error,
}

@injectable()
export class SubscriptionStore {
    @observable submitted = false;
    @observable optedInSent = false;
    @observable state = LoadState.NotLoaded;
    @observable email = '';
    @observable tenantId = '';
    @observable currentPartitionId = '' as string | undefined;

    optionsForm = new FormState({
        unsoldEstimates: new FieldState(false),
        agingEquipment: new FieldState(false),
        expiringMemberships: new FieldState(false),
        idleAccount: new FieldState(false),
        other: new FieldState(false),
        recurringService: new FieldState(false),
        newsletter: new FieldState(false),
        thankYou: new FieldState(false),
        customerWelcome: new FieldState(false),
        marketToLeads: new FieldState(false),
    });

    private payloadEncoded = '';
    private signatureEncoded = '';
    @observable private submittedPreferences = [] as SubscriptionOptions[];

    private optionsFormMap = new Map<SubscriptionOptions, FieldState<boolean>>([
        [SubscriptionOptions.IdleAccount, this.optionsForm.$.idleAccount],
        [SubscriptionOptions.ExpiringMemberships, this.optionsForm.$.expiringMemberships],
        [SubscriptionOptions.AgingEquipment, this.optionsForm.$.agingEquipment],
        [SubscriptionOptions.UnsoldEstimates, this.optionsForm.$.unsoldEstimates],
        [SubscriptionOptions.Other, this.optionsForm.$.other],
        [SubscriptionOptions.RecurringService, this.optionsForm.$.recurringService],
        [SubscriptionOptions.Newsletter, this.optionsForm.$.newsletter],
        [SubscriptionOptions.ThankYou, this.optionsForm.$.thankYou],
        [SubscriptionOptions.CustomerWelcome, this.optionsForm.$.customerWelcome],
        [SubscriptionOptions.MarketToLeads, this.optionsForm.$.marketToLeads],
    ]);

    constructor(
        @inject(Unsubscribe) private marketingApi: Unsubscribe,
        @inject(SubscriptionPreferences) private subscribeApi: SubscriptionPreferences,
        @inject(Validation) private validationApi: Validation,
        @inject(OptIn) private optInApi: OptIn
    ) {
        makeObservable(this);
    }

    async updatePreferences() {
        runInAction(() => {
            this.submittedPreferences = this.getAllOptionValues;
        });
        if (this.submittedPreferences.length > 0) {
            await this.sendPreferencesUpdateRequest();
        } else {
            await this.sendUnsubscribeRequest();
        }
        this.setSubmitted(true);
    }

    async sendDoubleOptIn() {
        await this.sendOptInRequest();
        this.setSubmitted(true);
        this.setOptedInSent(true);
    }

    @computed
    get getAllOptionValues() {
        return Array.from(this.optionsFormMap)
            .filter(kvp => kvp[1].value)
            .map(kvp => kvp[0]);
    }

    @computed
    get canSubmit() {
        const selectedValues = this.getAllOptionValues;
        return this.submittedPreferences.toString() !== selectedValues.toString();
    }

    @computed
    get sentUnsubscribe() {
        return this.submittedPreferences.length === 0 && !this.optedInSent;
    }

    async validatePayload(encodedMessage: string, signature: string) {
        const request: ValidateRequest = {
            encodedMessage,
            sign: signature,
        };

        try {
            const result = await this.validationApi.validateSendBackPayload(request);

            const payloadData = result.data;
            this.setEmail(payloadData.emailAddress);
            this.setTenantId(payloadData.tenantId);
            this.setCurrentPartitionId(payloadData.currentPartitionId);

            runInAction(() => {
                this.payloadEncoded = encodedMessage;
                this.signatureEncoded = signature;
                this.state = LoadState.Loaded;
            });
        } catch (error) {
            this.setState(LoadState.Error);
        }
    }

    @action
    setState = (state: LoadState) => {
        this.state = state;
    };

    @action
    setEmail = (email: string) => {
        this.email = email;
    };

    @action
    setTenantId = (tenantId: string) => {
        this.tenantId = tenantId;
    };

    @action
    setCurrentPartitionId = (currentPartitionId: string | undefined) => {
        this.currentPartitionId = currentPartitionId;
    };

    @action
    setSubmitted = (value: boolean) => {
        this.submitted = value;
    };

    @action
    setOptedInSent = (value: boolean) => {
        this.optedInSent = value;
    };

    @action
    setCurrentPreferences = (preferences: SubscriptionOptions[]) => {
        this.submittedPreferences = preferences;
        preferences.forEach(option => {
            this.optionsFormMap.get(option)?.onChange(true);
        });
    };

    private async sendPreferencesUpdateRequest() {
        const request: UpdateSubscriptionPreferenceRequest = {
            email: this.email,
            subscriptionPreferences: this.submittedPreferences,
            tenantId: this.tenantId,
            currentPartitionId: this.currentPartitionId,
            messageData: this.payloadEncoded,
            signature: this.signatureEncoded,
        };
        await this.subscribeApi.updateSubscriptionPreferences(request);
    }

    private async sendUnsubscribeRequest() {
        const request: UnsubscribeRequest = {
            email: this.email,
            tenantId: this.tenantId,
            currentPartitionId: this.currentPartitionId,
            messageData: this.payloadEncoded,
            signature: this.signatureEncoded,
        };

        await this.marketingApi.unsubscribe(request);
    }

    private async sendOptInRequest() {
        if (this.currentPartitionId === undefined) {
            throw new Error('Value cannot be undefined.');
        }

        const request: OptInRequest = {
            email: this.email,
            tenantId: this.tenantId,
            currentPartitionId: this.currentPartitionId,
            messageData: this.payloadEncoded,
            signature: this.signatureEncoded,
        };

        await this.optInApi.optIn(request);
    }
}
